import { useCallback, useMemo } from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import styled from 'styled-components';

import { Checkbox } from '../Checkbox';
import type {
  CheckboxValue,
  OnCheckboxChange,
} from '../Checkbox';

export interface CheckboxGroupOption extends Record<string, unknown> {
  help_text?: ReactNode;
  label?: ReactNode;
  value?: CheckboxValue;
  /**
   * Если true, то этот вариант невозможно убрать из списка
   */
  required?: boolean;
}

export type CheckboxGroupProps = {
  /**
   * инлайновый вывод чекбоксов
   */
  inline?: boolean;

  /**
  * name каждого html-элемента input
  */
  name?: string;

  /**
  * массив значений выбранных опций
  */
  value: CheckboxValue[];
  /**
  * выключена ли данная группа чекбоксов
  */
  disabled?: boolean;
  /**
   * список опций данной группы чекбоксов
   */
  options: CheckboxGroupOption[];

  /**
  * ключ, по которому хранятся значения в объектах options
  */
  valueKey?: string;
  /**
  * ключ, по которому хранятся заголовки в объектах options
  */
  labelKey?: string;
  /**
  * ключ, по которому хранится текст для всплывающей подсказки в объектах options
  */
  helpTextKey?: string;
  /**
   * ключ, по которому значения обязательности этой опции в объектах options
   */
  requiredKey?: string
  /**
  * хэндлер изменения значения
  */
  onChange?: (nextValue: CheckboxValue[]) => void;
};

export const StyledItemWrapper = styled.div<{
  $isInline: boolean;
}>(({
  $isInline,

  theme: {
    indents,
  },
}) => {
  if ($isInline) {
    return {
      display: 'inline-block',
      marginTop: 0,
      marginRight: indents.INDENT_2,
    };
  }

  return {
    marginTop: indents.INDENT_1,

    '&:first-child': {
      marginTop: 0,
    },
  };
});

export function CheckboxGroup({
  inline = false,

  name = '',
  value,
  disabled = false,
  options,

  helpTextKey = 'help_text',
  valueKey = 'value',
  labelKey = 'label',
  requiredKey = 'required',

  onChange: onChangeProp = undefined,
}: CheckboxGroupProps): ReactElement {
  const optionsMapByValue: Map<string | number, CheckboxGroupOption> = useMemo(() => {
    const optionsMap: Map<string | number, CheckboxGroupOption> = new Map();
    options.forEach((option) => {
      if (option.value) {
        optionsMap.set(option.value, option);
      }
    });

    return optionsMap;
  }, [options]);

  const onChange = useCallback<OnCheckboxChange>((checked, changedItemValue) => {
    if (!onChangeProp) {
      return;
    }

    if (checked) {
      onChangeProp([...value, changedItemValue]);
    } else {
      const option = optionsMapByValue.get(changedItemValue);
      onChangeProp(
        value.filter((valueItem) => valueItem !== changedItemValue && !option?.required),
      );
    }
  }, [
    value,
    onChangeProp,
    optionsMapByValue,
  ]);

  return (
    <div>
      {
        options.map((option) => {
          const optionValue = option[valueKey] as string | number;
          const optionLabel = option[labelKey as 'label'];
          const optionHelpText = option[helpTextKey as 'help_text'];
          const optionRequired = Boolean(option[requiredKey as 'required']);

          return (
            <StyledItemWrapper
              $isInline={inline}
              key={optionValue}
            >
              <Checkbox
                value={optionValue}
                checked={value.includes(optionValue) || optionRequired}
                disabled={disabled || optionRequired}
                helpText={optionHelpText}
                name={name}
                label={optionLabel}
                onChange={onChange}
              />
            </StyledItemWrapper>
          );
        })
      }
    </div>
  );
}
